import React, { useState, useRef } from "react";
import { DateRangePickerDate } from "../date_range_picker/date_range_picker_date";
import { isSameDay, differenceInMonths, isSameMonth } from "date-fns";

function getInitialMonths(selectedDate?: Date, selectedRange?: DateRange) {
  const initialMonths = [];
  const startDate = selectedDate || (selectedRange && selectedRange.startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  if (startDate) {
    const startMonth = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      1
    );

    const monthsToStart = differenceInMonths(startMonth, todayMonth);
    for (let i = 0; i <= monthsToStart; i++) {
      initialMonths.push(
        new Date(todayMonth.getFullYear(), todayMonth.getMonth() + i, 1)
      );
    }

    const lastMonth = initialMonths[initialMonths.length - 1];
    for (let i = 1; i <= 6; i++) {
      initialMonths.push(
        new Date(lastMonth.getFullYear(), lastMonth.getMonth() + i, 1)
      );
    }
  } else {
    for (let i = 0; i <= 6; i++) {
      initialMonths.push(
        new Date(todayMonth.getFullYear(), todayMonth.getMonth() + i, 1)
      );
    }
  }

  return initialMonths;
}

type SelectionMode = "single" | "range";

interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface ScrollableCalendarProps {
  selectedDate?: Date;
  selectedRange?: DateRange;
  onSelectDate?: (date: Date) => void;
  onSelectRange?: (range: DateRange) => void;
  selectionMode?: SelectionMode;
}

export function ScrollableCalendar({
  selectedDate,
  selectedRange,
  onSelectDate,
  onSelectRange,
  selectionMode = "single",
}: ScrollableCalendarProps) {
  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
  const containerRef = useRef<HTMLDivElement>(null);
  const [months, setMonths] = useState<Date[]>(
    getInitialMonths(selectedDate, selectedRange)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const loadMoreMonths = () => {
    if (isLoading) return;
    setIsLoading(true);

    const lastMonth = months[months.length - 1];
    const newMonths = [...months];

    for (let i = 1; i <= 12; i++) {
      const newMonth = new Date(
        lastMonth.getFullYear(),
        lastMonth.getMonth() + i,
        1
      );
      newMonths.push(newMonth);
    }

    setMonths(newMonths);
    setIsLoading(false);
  };

  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;

    if (scrollBottom < 1000) {
      loadMoreMonths();
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const calendar = [];
    let day = 1;

    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < startingDay) {
          week.push(null);
        } else if (day > daysInMonth) {
          week.push(null);
        } else {
          week.push(new Date(year, month, day));
          day++;
        }
      }
      calendar.push(week);
      if (day > daysInMonth) break;
    }

    return calendar;
  };

  const isDateInPast = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date: Date) => {
    if (isDateInPast(date)) return;

    if (selectionMode === "single") {
      onSelectDate?.(date);
    } else {
      if (
        selectedRange &&
        isSameDay(selectedRange.startDate, selectedRange.endDate)
      ) {
        const startDate = selectedRange.startDate;
        const endDate = date;

        const range = {
          startDate: startDate < endDate ? startDate : endDate,
          endDate: startDate < endDate ? endDate : startDate,
        };

        onSelectRange?.(range);
      } else {
        onSelectRange?.({ startDate: date, endDate: date });
      }
    }
  };

  const hasRange =
    !!selectedRange &&
    !isSameDay(selectedRange.startDate, selectedRange.endDate);

  const isInRange = (date: Date | null) => {
    if (!date || !selectedRange) return false;

    const range = selectedRange;
    if (!range || !range.startDate) return false;

    const start = range.startDate;
    const end = range.endDate || range.startDate;

    return date > start && date < end;
  };

  const isRangeStart = (date: Date | null) => {
    if (!date || !selectedRange) return false;
    const range = selectedRange;
    return range?.startDate?.toDateString() === date.toDateString() && hasRange;
  };

  const isRangeEnd = (date: Date | null) => {
    if (!date || !selectedRange) return false;
    const range = selectedRange;
    return range?.endDate?.toDateString() === date.toDateString() && hasRange;
  };

  const isSelectedDate = (date: Date | null) => {
    if (!date) return false;
    if (selectionMode === "single") {
      return selectedDate?.toDateString() === date.toDateString();
    }
    return (
      !!selectedRange &&
      (isSameDay(date, selectedRange.startDate) ||
        isSameDay(date, selectedRange?.endDate))
    );
  };

  const handleScrollToMonthRef = (ref: HTMLTableElement | null) => {
    if (hasScrolled || !ref) return;
    ref.scrollIntoView({ behavior: "smooth", block: "center" });
    setHasScrolled(true);
  };

  const isTargetMonth = (date: Date) => {
    const targetDate = selectedDate || selectedRange?.startDate;
    if (!targetDate) return false;
    return isSameMonth(date, targetDate);
  };

  return (
    <div
      className="overflow-auto position-relative w-100 vh-100"
      ref={containerRef}
      onScroll={handleScroll}
    >
      <table
        className="table table-borderless position-sticky top-0"
        style={{ zIndex: 10 }}
      >
        <thead>
          <tr className="border-bottom">
            {weekDays.map((day, index) => (
              <th key={index} className="text-center p-2">
                <div>{day}</div>
              </th>
            ))}
          </tr>
        </thead>
      </table>

      {months.map((monthDate) => (
        <div key={monthDate.toISOString()}>
          <table
            className="table table-borderless"
            ref={isTargetMonth(monthDate) ? handleScrollToMonthRef : undefined}
          >
            <tbody>
              <tr>
                <td colSpan={7} className="text-start">
                  <div className="fw-bold">
                    {monthDate.toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                </td>
              </tr>
              {getDaysInMonth(monthDate).map((week, weekIndex) => (
                <tr key={weekIndex}>
                  {week.map((date, dayIndex) => (
                    <td key={dayIndex} className="text-center p-0">
                      {date && (
                        <DateRangePickerDate
                          date={date.getDate()}
                          isDateInBetween={isInRange(date)}
                          isHighlighted={isSelectedDate(date)}
                          isDateFaded={isDateInPast(date)}
                          isSameFirstDate={isRangeStart(date)}
                          isSameSecondDate={isRangeEnd(date)}
                          onClick={() => date && handleDateClick(date)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
